import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { targetsApi, pipelineApi, formatApiError } from '../lib/api';
import { Link, useSearchParams } from 'react-router-dom';
import { HelpButton } from '../components/HelpPanel';
import { WriteOnly } from '../components/ViewerGuard';
import usePageTitle from '../hooks/usePageTitle';
import { getCategoryIcon } from '../components/CategoryIcons';
import { useTargetSync } from '../contexts/TargetSyncContext';
import {
  Search, Building, MapPin, ExternalLink, Calendar, Star,
  ChevronRight, Trash2, Loader2, CheckSquare, Square, ArrowUpDown, User, X,
  Filter, ChevronDown
} from 'lucide-react';

const titleCase = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const SORT_OPTIONS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'relevance_desc', label: 'Relevance High-Low' },
  { value: 'relevance_asc', label: 'Relevance Low-High' },
  { value: 'alpha_asc', label: 'Alphabetical A-Z' },
  { value: 'alpha_desc', label: 'Alphabetical Z-A' },
];

const TargetsPage = () => {
  usePageTitle('Targets');
  const { updatedTargets, syncTarget } = useTargetSync();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlOwner = searchParams.get('owner') || '';
  const urlStatus = searchParams.get('status') || '';
  const urlTypes = searchParams.get('types') || '';

  const [targets, setTargets] = useState([]);
  const [pipelineRecords, setPipelineRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Multi-select category filter (empty set = all). Persisted to the `types` URL param.
  const [selectedTypes, setSelectedTypes] = useState(() => new Set(urlTypes ? urlTypes.split(',').filter(Boolean) : []));
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryRef = useRef(null);
  const [filterOwner, setFilterOwner] = useState(urlOwner || 'all');
  const [filterStatus, setFilterStatus] = useState(urlStatus || 'all');
  const [sortBy, setSortBy] = useState('recent');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const fetchTargets = async () => {
    try {
      const [targetsRes, pipelineRes] = await Promise.all([
        targetsApi.getAll(),
        pipelineApi.getAll(),
      ]);
      setTargets(targetsRes.data);
      setPipelineRecords(pipelineRes.data);
    } catch (err) { console.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTargets(); }, []);

  // Sync URL params to state
  useEffect(() => {
    if (urlOwner) setFilterOwner(urlOwner);
    if (urlStatus) setFilterStatus(urlStatus);
  }, [urlOwner, urlStatus]);

  const clearTeamFilter = () => {
    setFilterOwner('all');
    setFilterStatus('all');
    setSearchParams({});
  };

  // Close the category dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) setShowCategoryDropdown(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const persistTypes = (nextSet) => {
    const params = new URLSearchParams(searchParams);
    if (nextSet.size) params.set('types', [...nextSet].join(',')); else params.delete('types');
    setSearchParams(params);
  };
  const toggleType = (type) => {
    const next = new Set(selectedTypes);
    if (next.has(type)) next.delete(type); else next.add(type);
    setSelectedTypes(next);
    persistTypes(next);
  };
  const clearTypes = () => {
    const next = new Set();
    setSelectedTypes(next);
    persistTypes(next);
  };

  const targetTypes = ['all', ...new Set(targets.map(t => t.type).filter(Boolean))];
  const categoryOptions = targetTypes.filter(t => t !== 'all');
  const owners = ['all', ...Array.from(new Map(
    targets.map(t => t.owner).filter(Boolean).map(o => [titleCase(o).trim().toLowerCase(), titleCase(o).trim()])
  ).values())];

  // Build a map: targetId -> pipeline status category
  const getTargetPipelineStatus = (targetId) => {
    const records = pipelineRecords.filter(r => r.target_id === targetId);
    if (records.length === 0) return null;
    // Return the "best" status: won > active > stalled > cold
    const hasWon = records.some(r => r.stage === 'closed_won');
    if (hasWon) return 'won';
    const hasCold = records.some(r => r.temperature === 'cold' || r.temperature === 'dormant');
    const hasStalled = records.some(r => {
      if (r.stage === 'closed_won' || r.stage === 'closed_lost') return false;
      // Check if last_contact_date is > 14 days old
      const lc = r.last_contact_date;
      if (!lc) return false;
      const lcDate = typeof lc === 'string' ? new Date(lc) : lc;
      return (Date.now() - lcDate.getTime()) > 14 * 24 * 60 * 60 * 1000;
    });
    if (hasCold) return 'cold';
    if (hasStalled) return 'stalled';
    return 'active';
  };

  const filteredTargets = targets
    .map(t => (updatedTargets[t.id] ? { ...t, ...updatedTargets[t.id] } : t))
    .filter(target => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = target.name.toLowerCase().includes(q) || target.type?.toLowerCase().includes(q);
      const matchesType = selectedTypes.size === 0 || selectedTypes.has(target.type);
      const matchesOwner = filterOwner === 'all' || (target.owner || '').toLowerCase() === filterOwner.toLowerCase() || (target.created_by_name || '').toLowerCase() === filterOwner.toLowerCase();
      let matchesStatus = true;
      if (filterStatus !== 'all') {
        const status = getTargetPipelineStatus(target.id);
        matchesStatus = status === filterStatus;
      }
      return matchesSearch && matchesType && matchesOwner && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'recent': {
          const da = a.research_date || a.created_at || '';
          const db2 = b.research_date || b.created_at || '';
          return db2 > da ? 1 : da > db2 ? -1 : 0;
        }
        case 'relevance_desc': return ((b.relevance_score_override ?? b.relevance_score) || 0) - ((a.relevance_score_override ?? a.relevance_score) || 0);
        case 'relevance_asc': return ((a.relevance_score_override ?? a.relevance_score) || 0) - ((b.relevance_score_override ?? b.relevance_score) || 0);
        case 'alpha_asc': return (a.name || '').localeCompare(b.name || '');
        case 'alpha_desc': return (b.name || '').localeCompare(a.name || '');
        default: return 0;
      }
    });

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredTargets.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredTargets.map(t => t.id)));
  };

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      await targetsApi.bulkDelete([...selectedIds]);
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      await fetchTargets();
    } catch (err) { console.error(formatApiError(err)); }
    finally { setBulkDeleting(false); }
  };

  const isSelectMode = selectedIds.size > 0;

  if (loading) {
    return (
      <div className="">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="card-surface h-48 shimmer" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-heading text-3xl text-white" data-testid="targets-title">Target Intelligence</h1>
            <HelpButton />
          </div>
          <p className="text-sm text-[#A3B5C7]/70">Your full intelligence database — every organisation and individual you have researched.</p>
        </div>
        <div className="flex items-center gap-2">
          <WriteOnly>
          {isSelectMode && (
            <button onClick={() => setShowBulkDeleteConfirm(true)} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-2" data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete Selected ({selectedIds.size})
            </button>
          )}
          </WriteOnly>
          {/* Sort Dropdown */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="input-field text-xs pr-8 appearance-none cursor-pointer"
              data-testid="sort-dropdown"
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ArrowUpDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3B5C7] pointer-events-none" />
          </div>
          <Link to="/deduplication" className="btn-secondary text-xs flex items-center gap-1.5" data-testid="dedup-link">Find Duplicates</Link>
          <Link
            to="/export/targets"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const { exportApi } = await import('../lib/api');
                const res = await exportApi.targetsCsv();
                const url = URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'targets_export.csv'; a.click(); URL.revokeObjectURL(url);
              } catch (err) { console.error(err); }
            }}
            className="btn-secondary text-xs"
            data-testid="export-targets-csv-btn"
          >Export CSV</Link>
        </div>
      </div>

      {/* Team Activity Filter Banner */}
      {(urlOwner || urlStatus) && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-[#00D4C8]/5 border border-[#00D4C8]/20 flex items-center justify-between" data-testid="team-filter-banner">
          <p className="text-sm text-[#00D4C8]">
            Filtered: <span className="text-white font-medium">{filterOwner !== 'all' ? filterOwner : 'All'}</span>
            {filterStatus !== 'all' && <> &middot; <span className="text-white font-medium capitalize">{filterStatus}</span></>}
          </p>
          <button onClick={clearTeamFilter} className="text-[#A3B5C7] hover:text-white transition-colors flex items-center gap-1 text-xs" data-testid="clear-team-filter-btn">
            <X className="w-3.5 h-3.5" /> Clear filter
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A3B5C7]" strokeWidth={1.5} />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search targets..." className="input-field has-icon" data-testid="targets-search" />
        </div>

        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {filteredTargets.length > 0 && (
            <button onClick={toggleSelectAll} className="px-3 py-2 text-xs rounded-lg bg-[#0E1C33] border border-[#1A2F4C] text-[#A3B5C7] hover:text-white transition-colors flex items-center gap-1.5" data-testid="select-all-btn">
              {selectedIds.size === filteredTargets.length ? <CheckSquare className="w-3.5 h-3.5 text-[#00D4C8]" /> : <Square className="w-3.5 h-3.5" />}
              {selectedIds.size === filteredTargets.length ? 'Deselect All' : 'Select All'}
            </button>
          )}

          {/* Category filter — collapsible multi-select dropdown */}
          <div className="relative" ref={categoryRef}>
            <button
              onClick={() => setShowCategoryDropdown(v => !v)}
              className={`px-4 py-2 text-sm rounded-lg border transition-colors flex items-center gap-2 ${selectedTypes.size > 0 ? 'bg-[#00D4C8]/10 border-[#00D4C8]/30 text-[#00D4C8]' : 'bg-[#0E1C33] border-[#1A2F4C] text-[#A3B5C7] hover:text-white'}`}
              data-testid="category-filter-toggle"
            >
              <Filter className="w-4 h-4" strokeWidth={1.5} />
              Filter by category
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#00D4C8]/20 text-[#00D4C8]">
                {selectedTypes.size > 0 ? selectedTypes.size : categoryOptions.length}
              </span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
            </button>
            {showCategoryDropdown && (
              <div className="absolute right-0 mt-2 w-80 max-h-[22rem] overflow-y-auto bg-[#0E1C33] border border-[#1A2F4C] rounded-lg shadow-2xl z-30 p-2" data-testid="category-filter-panel">
                <div className="flex items-center justify-between px-2 py-1.5">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#A3B5C7]/70">Categories ({categoryOptions.length})</span>
                  {selectedTypes.size > 0 && (
                    <button onClick={clearTypes} className="text-xs text-[#00D4C8] hover:underline" data-testid="category-clear-btn">Clear all</button>
                  )}
                </div>
                <button
                  onClick={clearTypes}
                  className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-[#0A1628] transition-colors"
                  data-testid="category-option-all"
                >
                  {selectedTypes.size === 0 ? <CheckSquare className="w-4 h-4 text-[#00D4C8] shrink-0" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-[#A3B5C7]/50 shrink-0" strokeWidth={1.5} />}
                  <span className="text-sm text-white">All categories</span>
                </button>
                {categoryOptions.map((type) => (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left hover:bg-[#0A1628] transition-colors"
                    data-testid={`category-option-${type}`}
                  >
                    {selectedTypes.has(type) ? <CheckSquare className="w-4 h-4 text-[#00D4C8] shrink-0" strokeWidth={1.5} /> : <Square className="w-4 h-4 text-[#A3B5C7]/50 shrink-0" strokeWidth={1.5} />}
                    <span className="text-sm text-[#D4E2F0]">{titleCase(type)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Owner filter */}
          {owners.map(o => (
            <button key={o} onClick={() => setFilterOwner(o)} className={`px-3 py-2 text-xs rounded-lg transition-colors flex items-center gap-1 ${filterOwner === o ? 'bg-[#00D4C8] text-[#0A1628]' : 'bg-[#0E1C33] border border-[#1A2F4C] text-[#A3B5C7] hover:text-white'}`}>
              {o !== 'all' && <User className="w-3 h-3" />}
              {o === 'all' ? 'All Owners' : o}
            </button>
          ))}

          {/* Pipeline status filter */}
          {filterOwner !== 'all' && (
            <>
              <span className="text-[#A3B5C7]/40 text-xs mx-1">|</span>
              {[
                { key: 'all', label: 'All Status' },
                { key: 'active', label: 'Active', color: '#00D4C8' },
                { key: 'won', label: 'Won', color: '#22C55E' },
                { key: 'stalled', label: 'Stalled', color: '#FFD93D' },
                { key: 'cold', label: 'Cold', color: '#6BCBFF' },
              ].map(s => (
                <button
                  key={s.key}
                  onClick={() => setFilterStatus(s.key)}
                  className={`px-3 py-2 text-xs rounded-lg transition-colors flex items-center gap-1.5 ${filterStatus === s.key ? 'bg-[#00D4C8] text-[#0A1628]' : 'bg-[#0E1C33] border border-[#1A2F4C] text-[#A3B5C7] hover:text-white'}`}
                  data-testid={`status-filter-${s.key}`}
                >
                  {s.color && <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />}
                  {s.label}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Active category chips (quick removal) */}
      {selectedTypes.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-6 -mt-2" data-testid="active-category-chips">
          {[...selectedTypes].map((type) => (
            <button key={type} onClick={() => toggleType(type)} className="px-2.5 py-1 text-xs rounded-full bg-[#00D4C8]/10 border border-[#00D4C8]/30 text-[#00D4C8] flex items-center gap-1.5 hover:bg-[#00D4C8]/20 transition-colors" data-testid={`active-category-chip-${type}`}>
              {titleCase(type)} <X className="w-3 h-3" />
            </button>
          ))}
        </div>
      )}

      {/* Targets Grid */}
      {filteredTargets.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#A3B5C7] mb-4">No targets found</p>
          <Link to="/research" className="btn-primary">Research New Target</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="targets-grid">
          {filteredTargets.map((target, index) => {
            const isSelected = selectedIds.has(target.id);
            return (
              <motion.div key={target.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index * 0.03, 0.3) }} className="relative">
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleSelect(target.id); }} className={`absolute top-3 left-3 z-10 p-1 rounded transition-all ${isSelected ? 'text-[#00D4C8]' : 'text-[#A3B5C7]/40 hover:text-[#A3B5C7]'}`} data-testid={`target-checkbox-${target.id}`}>
                  {isSelected ? <CheckSquare className="w-5 h-5" strokeWidth={1.5} /> : <Square className="w-5 h-5" strokeWidth={1.5} />}
                </button>
                <Link to={`/targets/${target.id}`} className={`block card-surface p-5 pl-10 hover:border-[#00D4C8]/30 transition-colors group ${isSelected ? 'border-[#00D4C8]/30 bg-[#00D4C8]/5' : ''}`} data-testid={`target-card-${target.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#00D4C8]/10 flex items-center justify-center">
                        {(() => { const Icon = getCategoryIcon(target.type); return <Icon className="w-5 h-5 text-[#00D4C8]" />; })()}
                      </div>
                      <div>
                        <h3 className="text-white font-medium group-hover:text-[#00D4C8] transition-colors">{target.name}</h3>
                        <p className="text-xs text-[#A3B5C7]">{titleCase(target.type)}</p>
                      </div>
                    </div>
                    {(() => {
                      const relScore = target.relevance_score_override ?? target.relevance_score;
                      const isOverridden = target.relevance_score_override != null;
                      return relScore ? (
                        <div className="flex items-center gap-1" data-testid={`target-relevance-${target.id}`} title={isOverridden ? 'Manually edited score' : undefined}>
                          <Star className="w-4 h-4 text-[#00D4C8]" strokeWidth={1.5} />
                          <span className="text-sm text-[#00D4C8]">{Math.min(relScore, 5)}</span>
                          {isOverridden && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" data-testid={`target-relevance-edited-${target.id}`} />}
                        </div>
                      ) : null;
                    })()}
                  </div>
                  {target.hq_location && (
                    <div className="flex items-center gap-2 text-xs text-[#A3B5C7] mb-2">
                      <MapPin className="w-3 h-3" strokeWidth={1.5} /> {target.hq_location}
                    </div>
                  )}
                  {target.bd_work_types?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {target.bd_work_types.slice(0, 2).map((type) => (
                        <span key={type} className="px-2 py-0.5 text-[10px] uppercase tracking-wide bg-[#00D4C8]/10 text-[#00D4C8] rounded">{type}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-[#A3B5C7]">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" strokeWidth={1.5} />
                        {formatDate(target.research_date)}
                      </div>
                      {(target.owner || target.created_by_name) && (
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" strokeWidth={1.5} />
                          {titleCase(target.owner || target.created_by_name)}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-[#00D4C8]" />
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Bulk Delete Confirmation */}
      <AnimatePresence>
        {showBulkDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="bulk-delete-confirm-modal">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-[#0E1C33] border border-[#1A2F4C] rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><Trash2 className="w-5 h-5 text-red-400" strokeWidth={1.5} /></div>
                <h3 className="font-heading text-lg text-white">Delete {selectedIds.size} Target{selectedIds.size > 1 ? 's' : ''}</h3>
              </div>
              <p className="text-[#D4E2F0] text-sm leading-relaxed mb-2">Are you sure? This removes them from pipeline and cannot be undone.</p>
              <div className="max-h-32 overflow-y-auto mb-4 space-y-1">
                {targets.filter(t => selectedIds.has(t.id)).map(t => (
                  <div key={t.id} className="text-xs text-[#A3B5C7] pl-3 border-l-2 border-red-400/30">{t.name}</div>
                ))}
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowBulkDeleteConfirm(false)} className="btn-secondary text-sm" data-testid="bulk-delete-cancel-btn">Cancel</button>
                <button onClick={handleBulkDelete} disabled={bulkDeleting} className="px-4 py-2 rounded-lg text-sm font-medium bg-red-500 hover:bg-red-600 text-white transition-colors flex items-center gap-2" data-testid="bulk-delete-confirm-btn">
                  {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Delete Target{selectedIds.size > 1 ? 's' : ''}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TargetsPage;
